import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { broadcastHydrationUpdate, refetchHydrationData } from '@/lib/hydrationSync';
import { useToast } from '@/hooks/use-toast';
// notificationService removed - replaced with OneSignal
import { areNotificationsEnabled } from '@/lib/notificationPermission';

/**
 * Enhanced water logging hook that integrates with the notification service
 * This ensures deadline reset logic is applied when water is logged
 */
export function useWaterLogging() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const logWaterMutation = useMutation({
    mutationFn: async (data: {
      volume: number;
      source?: string;
      notificationId?: string;
      notificationNumber?: number;
    }) => {
      const response = await apiRequest('POST', '/api/water-intake', data);
      return response.json();
    },
    onSuccess: async (data, variables) => {
      console.log('✅ Water logged successfully:', data);

      // Force immediate refetch by invalidating and then refetching
      // This ensures data is updated even with staleTime: Infinity
      // Use local date for consistency with dashboard
      const today = new Date().toLocaleDateString('en-CA');
      console.log('🔄 Invalidating and refetching queries for date:', today);

      await refetchHydrationData(queryClient, 'in-app water log', { skipDebounce: true });
      broadcastHydrationUpdate('in-app water log');

      // Show success toast
      toast({
        title: '💧 Water Logged!',
        description: `Successfully logged ${variables.volume}ml.`,
        duration: 3000,
      });
    },
    onError: (error: any) => {
      console.error('❌ Failed to log water:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to log water intake. Please try again.',
        variant: 'destructive',
      });
    },
  });

  /**
   * Log water with volume only (simplified interface)
   */
  const logWater = (volume: number, source?: string) => {
    logWaterMutation.mutate({ volume, source });
  };

  /**
   * Log water from OneSignal notification (uses special endpoint)
   */
  const logWaterFromNotification = async (volume: number) => {
    try {
      const response = await apiRequest('POST', '/api/onesignal/log-from-notification', { volume });
      const data = await response.json();

      // Force immediate refetch by invalidating and then refetching
      // Use local date for consistency with dashboard
      const today = new Date().toLocaleDateString('en-CA');
      console.log('🔄 Invalidating and refetching queries for date:', today);

      await refetchHydrationData(queryClient, 'notification water log', { skipDebounce: true });
      broadcastHydrationUpdate('notification water log');

      toast({
        title: '💧 Water Logged from Notification!',
        description: `Successfully logged ${volume}ml.`,
        duration: 3000,
      });

      return data;
    } catch (error: any) {
      console.error('❌ Failed to log water from notification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to log water. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return {
    logWater,
    logWaterFromNotification,
    logWaterMutation,
    isLogging: logWaterMutation.isPending,
  };
}

/**
 * Hook for initializing notification service with profile data
 * REMOVED - will be replaced with OneSignal
 */
